/**
 * Cadastrar um cliente à mão (H17).
 *
 * ── Por que num diálogo, e não no lugar como a edição ─────────────────────
 *
 * A edição acontece dentro da ficha do cliente, e ali tampar a tela seria
 * cobrir justamente o que se está conferindo. Aqui é o contrário: a lista
 * atrás não ajuda em nada a preencher um cadastro novo, e um formulário
 * empurrando mil linhas para baixo faria a pessoa perder o lugar em que
 * estava.
 *
 * ── A recusa de duplicata é a mensagem mais importante da tela ────────────
 *
 * O servidor recusa telefone, e-mail ou documento que já existam, e a recusa
 * NOMEIA quem já está lá. É o desfecho mais provável depois do sucesso — quem
 * cadastra à mão costuma estar cadastrando alguém que já ligou antes —, e por
 * isso ela aparece inteira, e não como "não foi possível salvar".
 *
 * ── Depois de criar, a ficha ABRE ─────────────────────────────────────────
 *
 * E não "fecha e atualiza a lista". A lista abre no recorte "Esfriando", onde
 * um cliente recém-criado não entra: ele não tem negócio aberto. Fechar o
 * diálogo devolveria a pessoa a uma tela em que nada mudou, e o cadastro
 * pareceria não ter acontecido.
 */

import { useState, type ReactNode } from 'react';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import type { MudancaDeCampos } from '@cliqcrm/contracts';
import { useApi, useCamposPersonalizados } from '@/shared/api/consultas';
import { CamposEditaveis, obrigatoriosVazios } from '@/entities/campos/campo-personalizado';
import { Botao, Campo, Dialogo } from '@/shared/ui';

/**
 * A frase de cada recusa que o servidor NÃO pode detalhar.
 *
 * A API só deixa sair o `detail` de códigos cujo texto fala do pedido de quem
 * chamou — os outros carregam interior nosso: mensagem de engine, endereço de
 * serviço, existência de registro. Isso é certo, e cobra uma frase daqui.
 */
const FRASE_POR_CODIGO: Record<string, string> = {
  ENGINE_INDISPONIVEL:
    'Não é possível guardar CPF nem CNPJ agora. Cadastre sem o documento e avise o suporte.',
  SEM_PERMISSAO: 'Seu papel não cadastra cliente. Peça a um gestor ou a quem administra.',
  LIMITE_EXCEDIDO: 'Muitos cadastros seguidos. Espere um instante e tente de novo.',
  IDEMPOTENCIA_DIVERGENTE:
    'Este cadastro já foi enviado com outros dados. Feche e abra o formulário de novo.',
};

const GENERICA = 'Não foi possível cadastrar. Tente de novo; se continuar, avise o suporte.';

export function NovoCliente({
  aoFechar,
  aoCriar,
}: {
  aoFechar: () => void;
  /** O cliente que nasceu — a lista abre a ficha dele. */
  aoCriar: (clienteId: string) => void;
}): ReactNode {
  const api = useApi();
  const [nome, definirNome] = useState('');
  const [email, definirEmail] = useState('');
  const [telefone, definirTelefone] = useState('');
  const [cargo, definirCargo] = useState('');
  const [documento, definirDocumento] = useState('');
  const [salvando, definirSalvando] = useState(false);
  const [recusa, definirRecusa] = useState<string | null>(null);
  // Os campos personalizados do cliente (#568): os obrigatórios entram no cadastro.
  const campos = useCamposPersonalizados('cliente');
  const [mudancaDosCampos, definirMudancaDosCampos] = useState<MudancaDeCampos>({});
  const faltando = obrigatoriosVazios(campos.data?.itens ?? [], {}, mudancaDosCampos);

  const enviar = () => {
    if (!api || salvando) return;

    /*
     * A guarda do NOME mora aqui, e não só no botão.
     *
     * `Enter` num campo submete o formulário direto, sem passar pelo botão —
     * então desabilitar o botão não impede o envio. No staging isso produziu
     * três `REQUISICAO_INVALIDA` seguidos: a pessoa apertava Enter, o servidor
     * recusava um corpo sem nome, e a tela mostrava um erro genérico sobre um
     * campo que ela nem tinha percebido estar vazio.
     */
    if (nome.trim() === '') {
      definirRecusa('Informe o nome. Sem ele o cliente some da lista e da busca.');
      return;
    }

    definirSalvando(true);
    definirRecusa(null);

    void api
      .criarCliente({
        corpo: {
          nome: nome.trim(),
          // Campo vazio não vira `""`: o servidor guardaria uma string vazia
          // onde "não informado" é a leitura certa, e a busca por e-mail
          // passaria a casar com todo mundo que não tem e-mail.
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(telefone.trim() ? { telefone: telefone.trim() } : {}),
          ...(cargo.trim() ? { cargo: cargo.trim() } : {}),
          ...(documento.trim() ? { documento: documento.trim() } : {}),
          ...(Object.keys(mudancaDosCampos).length > 0 ? { campos: mudancaDosCampos } : {}),
        },
        /*
         * A chave é gerada UMA vez por envio, e não por tentativa de digitação.
         *
         * É o que faz o toque duplo no botão — ou a rede repetindo o pedido —
         * criar um cliente só. Sem ela, duas requisições simultâneas passariam
         * as duas pela recusa de duplicata, porque nenhuma das duas veria a
         * outra ainda gravada.
         */
        idempotencyKey:
          globalThis.crypto?.randomUUID?.() ?? `cliente-${Date.now()}-${Math.random()}`,
      })
      .then((criado) => aoCriar(criado.id))
      .catch((erro: unknown) => {
        definirRecusa(recadoDeErro(erro, { generica: GENERICA, porCodigo: FRASE_POR_CODIGO }));
        definirSalvando(false);
      });
  };

  const podeSalvar = nome.trim() !== '' && !salvando && faltando.length === 0;

  return (
    <Dialogo
      titulo="Novo cliente"
      forma="lateral"
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="fantasma" type="button" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="primario"
            type="submit"
            form="form-novo-cliente"
            desabilitado={!podeSalvar}
            motivo={
              salvando
                ? 'Cadastrando…'
                : nome.trim() === ''
                  ? 'O nome é obrigatório.'
                  : `Preencha ${faltando.map((campo) => campo.nome).join(', ')}.`
            }
          >
            {salvando ? 'Cadastrando…' : 'Cadastrar'}
          </Botao>
        </>
      }
    >
      <form
        id="form-novo-cliente"
        className="c360__formulario"
        onSubmit={(evento) => {
          evento.preventDefault();
          enviar();
        }}
      >
        <Campo
          id="novo-cliente-nome"
          rotulo="Nome"
          type="text"
          value={nome}
          maxLength={200}
          required
          autoFocus
          ajuda="Obrigatório. Sem nome, o cliente some da lista e da busca."
          onChange={(e) => definirNome(e.currentTarget.value)}
        />
        <Campo
          id="novo-cliente-telefone"
          rotulo="Telefone"
          type="tel"
          value={telefone}
          maxLength={40}
          ajuda="Com DDD."
          onChange={(e) => definirTelefone(e.currentTarget.value)}
        />
        <Campo
          id="novo-cliente-email"
          rotulo="E-mail"
          type="email"
          value={email}
          maxLength={200}
          onChange={(e) => definirEmail(e.currentTarget.value)}
        />
        <Campo
          id="novo-cliente-cargo"
          rotulo="Cargo"
          type="text"
          value={cargo}
          maxLength={120}
          onChange={(e) => definirCargo(e.currentTarget.value)}
        />
        <Campo
          id="novo-cliente-documento"
          rotulo="CPF ou CNPJ"
          type="text"
          inputMode="numeric"
          value={documento}
          maxLength={30}
          ajuda="Guardado cifrado. Só o dígito verificador é conferido na hora."
          onChange={(e) => definirDocumento(e.currentTarget.value)}
        />

        <CamposEditaveis
          id="novo-cliente-campo"
          campos={campos.data?.itens ?? []}
          valores={{}}
          mudanca={mudancaDosCampos}
          aoMudar={definirMudancaDosCampos}
        />

        {recusa && (
          <p className="c360__erro" role="alert">
            {recusa}
          </p>
        )}
      </form>
    </Dialogo>
  );
}
